import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { SupplierRecord } from '../../types';

interface SupplierAutoCompleteProps {
  value: SupplierRecord | null;
  onChange: (supplier: SupplierRecord | null) => void;
  disabled?: boolean;
  currentSupplierName?: string;
  currentSupplierAbn?: string;
  suppliersData?: { data: SupplierRecord[] };
  isLoadingSuppliers: boolean;
}

export const SupplierAutoComplete: React.FC<SupplierAutoCompleteProps> = ({
  value,
  onChange,
  disabled = false,
  currentSupplierName = '',
  currentSupplierAbn = '',
  suppliersData,
  isLoadingSuppliers,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingSupplier, setPendingSupplier] = useState<SupplierRecord | null>(null);

  // Filter suppliers based on search term
  const options = useMemo(() => {
    if (!suppliersData?.data) return [];

    const suppliers = suppliersData.data;
    if (!searchTerm.trim()) return suppliers;

    const term = searchTerm.toLowerCase();
    return suppliers.filter((supplier: SupplierRecord) =>
      supplier.name.toLowerCase().includes(term) ||
      (supplier.abn && supplier.abn.includes(term))
    );
  }, [suppliersData, searchTerm]);

  // Handle supplier change with confirmation
  const handleSupplierChange = (supplier: SupplierRecord | null) => {
    if (!supplier) {
      // If clearing the supplier, don't show confirmation
      onChange(supplier);
      return;
    }

    // Check if this is actually a change from the current values
    const isDifferentFromCurrent =
      supplier.name !== currentSupplierName ||
      supplier.abn !== currentSupplierAbn;

    if (isDifferentFromCurrent && (currentSupplierName || currentSupplierAbn)) {
      // Show confirmation dialog if there's a current supplier and it's different
      setPendingSupplier(supplier);
      setShowConfirmationDialog(true);
    } else {
      // No confirmation needed for new selection or same as current
      onChange(supplier);
    }
  };

  // Confirm supplier change
  const handleConfirmSupplierChange = () => {
    if (pendingSupplier) {
      onChange(pendingSupplier);
      setShowConfirmationDialog(false);
      setPendingSupplier(null);
    }
  };

  // Cancel supplier change
  const handleCancelSupplierChange = () => {
    setShowConfirmationDialog(false);
    setPendingSupplier(null);
  };

  
  // Render input with loading state
  const renderInput = (params: any) => (
    <TextField
      {...params}
      label="Supplier"
      variant="outlined"
      size="small"
      sx={{ width: '100%' }}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {isLoadingSuppliers && (
              <CircularProgress color="inherit" size={20} />
            )}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        value={value}
        onChange={(_: any, newValue: SupplierRecord | null) => {
          handleSupplierChange(newValue);
        }}
        options={options}
        getOptionLabel={(option: SupplierRecord) => option.name}
        renderOption={(props, option: SupplierRecord) => {
          const { key, ...restProps } = props;
          return (
            <li key={key} {...restProps}>
              <div>
                <div style={{ fontWeight: 500 }}>{option.name}</div>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>
                  {option.abn || '[No ABN]'}
                </div>
              </div>
            </li>
          );
        }}
        renderInput={renderInput}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={isLoadingSuppliers}
        disabled={disabled}
        onInputChange={(_, newInputValue) => {
          setSearchTerm(newInputValue);
        }}
        noOptionsText={searchTerm ? "No suppliers found" : "Type to search suppliers..."}
      />

      {/* Confirmation Dialog */}
      {showConfirmationDialog && pendingSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Change Supplier</h3>
                  <p className="text-sm text-gray-500">This action will update the invoice</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-3">
                  Are you sure you want to change the supplier for this invoice?
                </p>

                <div className="space-y-3">
                  {currentSupplierName && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current Supplier</p>
                      <p className="font-medium text-gray-900">{currentSupplierName}</p>
                      {currentSupplierAbn && (
                        <p className="text-sm text-gray-600">ABN: {currentSupplierAbn}</p>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-500 uppercase tracking-wide mb-1">New Supplier</p>
                    <p className="font-medium text-blue-900">{pendingSupplier.name}</p>
                    {pendingSupplier.abn && (
                      <p className="text-sm text-blue-700">ABN: {pendingSupplier.abn}</p>
                    )}
                  </div>
                </div>

                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    This will update the supplier name and ABN fields for this invoice.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelSupplierChange}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSupplierChange}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Change Supplier
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};